<template>
  <div class="report-selector-container">
    <div class="selector-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <p class="subtitle">{{ pageSubtitle }}</p>
    </div>

    <v-card class="selector-card" elevation="0">
      <v-card-text>
        <div class="selectors-grid">
          <!-- Workspace Select -->
          <!-- <div class="selector-wrapper">
            <label class="selector-label">Workspace</label>
            <div class="custom-select-wrapper">
              <select
                v-model="selectedWorkspaceId"
                @change="onWorkspaceChange"
                :disabled="loadingWorkspaces"
                class="native-select"
              >
                <option value="">Select a workspace</option>
                <option
                  v-for="ws in workspaces"
                  :key="ws.value"
                  :value="ws.value"
                >
                  {{ ws.name }}
                </option>
              </select>
              <v-icon class="select-icon">mdi-folder</v-icon>
            </div>
          </div> -->

          <!-- Report Select -->
          <!-- <div class="selector-wrapper">
            <label class="selector-label">Report</label>
            <div class="custom-select-wrapper">
              <select
                v-model="selectedReportId"
                @change="onReportChange"
                :disabled="!selectedWorkspaceId"
                class="native-select"
              >
                <option value="">Select a report</option>
                <option v-for="report in reports" :key="report.value" :value="report.value">
                  {{ report.name }}
                </option>
              </select>
              <v-icon class="select-icon">mdi-chart-box-outline</v-icon>
            </div>
          </div> -->

          <!-- Brand Select -->
          <div class="selector-wrapper">
            <label class="selector-label">Report</label>
            <div class="custom-select-wrapper">
              <select
                v-model="selectedAppId"
                @change="onBrandChange"
                :disabled="!selectedReportId"
                class="native-select"
              >
                <option value="">Select a report</option>
                <option v-for="brand in brands" :key="brand.value" :value="brand.value">
                  {{ brand.name }}
                </option>
              </select>
              <v-icon class="select-icon">mdi-tag-outline</v-icon>
            </div>
          </div>

          <!-- Loading indicators -->
          <div v-if="loadingWorkspaces || loadingReports" class="loading-text">
            <v-progress-circular indeterminate size="20" class="mr-2" />
            {{  "Loading reports..." }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <div class="embed-wrapper" v-if="embedConfig || loadingEmbed">
      <!-- Loading State -->
      <v-card v-if="loadingEmbed" class="embed-loading-card" elevation="8">
        <v-card-text class="text-center py-16">
          <v-progress-circular
            indeterminate
            size="90"
            width="8"
            color="#6d28d9"
          />
          <h2 class="mt-6 text-h5 font-weight-bold text-purple-darken-2">
            Cargando reporte...
          </h2>
          <p class="text-body-1 text-grey-darken-1 mt-2">
            Por favor espera mientras se carga el contenido
          </p>
        </v-card-text>
      </v-card>

      <!-- Embedded Report -->
      <v-card v-else class="embed-report-card" elevation="12">
        <!-- Header -->
        <div class="embed-header">
          <div class="header-content">
            <v-icon size="36" color="white">mdi-chart-timeline-variant</v-icon>
            <div class="ml-3">
              <h2 class="report-title">
                {{ selectedReport?.name || "Power BI Report" }}
              </h2>
              <p class="report-subtitle">
                {{ selectedWorkspace?.name || "Workspace" }}
              </p>
            </div>
          </div>

         <v-btn 
            icon 
            variant="text" 
            color="white" 
            title="Pantalla Completa"
            @click="toggleFullScreen"
          >
            <v-icon>mdi-fullscreen</v-icon>
          </v-btn>
        </div>

        <!-- Report Container -->
        <div ref="reportContainer" class="powerbi-embed-container"></div>
      </v-card>
    </div>

    <!-- Empty State (when no report selected) -->
    <v-card v-else class="empty-state-card" elevation="6">
      <v-card-text class="text-center py-16">
        <v-icon size="100" color="grey lighten-2" class="mb-6">
          mdi-chart-box-outline
        </v-icon>
        <h2 class="text-h5 font-weight-medium text-grey-darken-2 mb-3">
          Selecciona un reporte
        </h2>
        <p class="text-body-1 text-grey-darken-1">
          Elige un workspace y un reporte para visualizarlo aquí
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, defineProps, defineEmits } from "vue";
import ApiService from "@/services/apiService";
import * as pbi from "powerbi-client";

// Props
const props = defineProps({
  currentRequester: {
    type: String,
    required: true,
    validator: (value) => ['interno', 'prod', 'test'].includes(value)
  },
  pageTitle: {
    type: String,
    default: 'PBI Report Viewer'
  },
  pageSubtitle: {
    type: String,
    default: 'Select workspace and reports'
  },
  brandLabel: {
    type: String,
    default: 'Brand'
  },
  filterConfig: {
    type: Object,
    default: () => ({
      table: 'surveys',
      column: 'idApp'
    })
  },
  embedSettings: {
    type: Object,
    default: () => ({
      background: pbi.models.BackgroundType.Default,
      localeSettings: { language: 'es' },
      panes: {
        filters: { expanded: false, visible: false },
        pageNavigation: { visible: true }
      }
    })
  }
});

// Emits
const emit = defineEmits(['workspace-changed', 'report-changed', 'brand-changed', 'report-loaded']);

// PowerBI Service
const powerbi = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

// State
const workspaces = ref([]);
const reports = ref([]);
const brands = ref([]);
const selectedWorkspaceId = ref("");
const selectedReportId = ref("");
const selectedAppId = ref("");
const selectedReportEmbedUrl = ref("");
const selectedReportAccessToken = ref("");
const loadingWorkspaces = ref(false);
const loadingReports = ref(false);
const loadingEmbed = ref(false);
const embedConfig = ref(null);
const reportContainer = ref(null);
let embeddedReport = null;

// Computed
const selectedWorkspace = computed(() => {
  return workspaces.value.find(ws => ws.value === selectedWorkspaceId.value) || null;
});

const selectedReport = computed(() => {
  return reports.value.find(r => r.value === selectedReportId.value) || null;
});

// Methods
const getToken = async () => {
  try {
    loadingWorkspaces.value = true;
    
    const tokenRes = await ApiService.get("/powerbi/generate-token");
    if (!tokenRes.success) {
      throw new Error("Failed to generate Power BI token");
    }

    const workspaceRes = await ApiService.get("/powerbi/get-workspaces");
    if (!workspaceRes.success) {
      throw new Error("Failed to load workspaces");
    }

    workspaces.value = workspaceRes.data || [];
  } catch (error) {
    console.error("Error loading workspaces:", error);
    alert(error.message || "Error loading data");
  } finally {
    loadingWorkspaces.value = false;
  }
};

// function for workspace change
// const onWorkspaceChange = async () => {
//   // Reset downstream selections
//   selectedReportId.value = "";
//   selectedAppId.value = "";
//   reports.value = [];
//   brands.value = [];
//   resetEmbed();

//   if (!selectedWorkspaceId.value) return;

//   loadingReports.value = true;
//   try {
//     const reportResponse = await ApiService.post("/powerbi/get-reports", {
//       workspaceId: selectedWorkspaceId.value
//     });

//     if (reportResponse.success) {
//       reports.value = reportResponse.data || [];
//       emit('workspace-changed', {
//         workspaceId: selectedWorkspaceId.value,
//         workspace: selectedWorkspace.value
//       });
//     }
//   } catch (error) {
//     console.error("Error fetching reports:", error);
//     alert("Failed to load reports");
//   } finally {
//     loadingReports.value = false;
//   }
// };


// function for report change
// const onReportChange = async () => {
//   // Reset downstream selections
//   selectedAppId.value = "";
//   brands.value = [];
//   resetEmbed();

//   if (!selectedReportId.value) return;

//   try {
//     const selectedReport = reports.value.find(r => r.value === selectedReportId.value);
//     if (!selectedReport) return;

//     selectedReportEmbedUrl.value = selectedReport.embedUrl;

//     // Generate access token
//     const accessTokenResponse = await ApiService.post("/powerbi/generate-access-token", {
//       workspaceId: selectedWorkspaceId.value,
//       reportId: selectedReportId.value
//     });

//     if (!accessTokenResponse.success) {
//       throw new Error("Failed to generate access token");
//     }

//     selectedReportAccessToken.value = accessTokenResponse.data;

//     // Get filters/brands
//     const filterResponse = await ApiService.post("/powerbi/generate-filters", {
//       currentRequester: props.currentRequester
//     });

//     if (!filterResponse.success) {
//       throw new Error("Failed to generate filters");
//     }

//     brands.value = filterResponse.data || [];
    
//     emit('report-changed', {
//       reportId: selectedReportId.value,
//       report: selectedReport,
//       brands: brands.value
//     });
//   } catch (error) {
//     console.error("Error handling report change:", error);
//     alert(error.message || "Failed to load report data");
//   }
// };


const onBrandChange = async () => {
  if (!selectedAppId.value) {
    console.warn("No brand selected");
    return;
  }

  try {
    const selectedBrand = brands.value.find(b => b.value === selectedAppId.value);
    
    if (!selectedBrand) {
      console.error("Brand not found:", selectedAppId.value);
      throw new Error("Failed to find selected brand");
    }

    if (!selectedReportAccessToken.value) {
      throw new Error("Missing access token");
    }

    if (!selectedReportEmbedUrl.value) {
      throw new Error("Missing embed URL");
    }

    loadingEmbed.value = false;

    const config = {
      type: "report",
      tokenType: pbi.models.TokenType.Embed,
      accessToken: selectedReportAccessToken.value,
      embedUrl: selectedReportEmbedUrl.value,
      id: selectedReportId.value,
      filters: [
        {
          $schema: "http://powerbi.com/power-bi-filter",
          target: {
            table: props.filterConfig.table,
            column: props.filterConfig.column
          },
          operator: "In",
          values: [selectedAppId.value],
          filterType: pbi.models.FilterType.Basic
        }
      ],
      settings: props.embedSettings
    };

    embedConfig.value = config;

    emit('brand-changed', {
      brandId: selectedAppId.value,
      brand: selectedBrand
    });

    await nextTick();
    embedReport();
  } catch (error) {
    console.error("Error in onBrandChange:", error);
    alert(error.message || "Error loading data");
    loadingEmbed.value = false;
  }
};

const getActiveWorkspace = async() => {
  try{
    const activeWorkspace = await ApiService.get("/powerbi/get-active");

    if(activeWorkspace.status === 200 && activeWorkspace.success) {
        selectedWorkspaceId.value = activeWorkspace?.data?.[0]?.workspace_id || "";
        selectedReportId.value = activeWorkspace?.data?.[0]?.report_id || "";
        selectedReportEmbedUrl.value = activeWorkspace?.data?.[0]?.report_embed_url || ""

      // Generate access token
      const accessTokenResponse = await ApiService.post("/powerbi/generate-access-token", {
        workspaceId: selectedWorkspaceId.value,
        reportId: selectedReportId.value
      });

      if (!accessTokenResponse.success) {
        throw new Error("Failed to generate access token");
      }

      selectedReportAccessToken.value = accessTokenResponse.data;

      // Get filters/brands
      const filterResponse = await ApiService.post("/powerbi/generate-filters", {
        currentRequester: props.currentRequester
      });

      if (!filterResponse.success) {
        throw new Error("Failed to generate filters");
      }

      brands.value = filterResponse.data || [];
      
      emit('report-changed', {
        reportId: selectedReportId.value,
        report: selectedReport,
        brands: brands.value
      });
  }
  }catch(error) {
    console.log("Error: ", error);
    alert('algo salió mal');
  }
}

const resetEmbed = () => {
  if (reportContainer.value && embeddedReport) {
    powerbi.reset(reportContainer.value);
    embeddedReport = null;
  }
  embedConfig.value = null;
};

const embedReport = () => {
  if (!reportContainer.value) {
    console.error("Report container not found!");
    loadingEmbed.value = false;
    return;
  }
  
  if (!embedConfig.value) {
    console.error("Embed config is missing!");
    loadingEmbed.value = false;
    return;
  }

  try {
    powerbi.reset(reportContainer.value);
    
    embeddedReport = powerbi.embed(reportContainer.value, embedConfig.value);
    

    embeddedReport.on("loaded", () => {
      loadingEmbed.value = false;
      emit('report-loaded', { report: embeddedReport });
    });
    
    embeddedReport.on("rendered", () => {
      console.log("Report fully rendered");
    });
    
    embeddedReport.on("error", (e) => {
      console.error("❌ Power BI Error:", e.detail);
      loadingEmbed.value = false;
      alert("Error loading Power BI report. Check console for details.");
    });
  } catch (error) {
    console.error("❌ Exception in embedReport:", error);
    loadingEmbed.value = false;
    alert("Failed to embed report: " + error.message);
  }
};

const toggleFullScreen = () => {
  if (embeddedReport) {
    try {
      embeddedReport.fullscreen();
    } catch (error) {
      console.error("Error entering fullscreen:", error);
    }
  } else {
    console.warn("Report not loaded yet.");
  }
};

// Lifecycle
onMounted(async () => {
  await getToken();
  await getActiveWorkspace()
});
</script>

<style scoped>
.report-selector-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f5f7 0%, #efefef 100%);
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
}

.selector-header {
  margin-bottom: 3rem;
  text-align: center;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.subtitle {
  font-size: 1rem;
  color: #666;
}

.selector-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 2rem;
  border: 1px solid #e5e5e5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.selectors-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
}

@media (max-width: 768px) {
  .selectors-grid {
    grid-template-columns: 1fr;
  }
}

.selector-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selector-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.custom-select-wrapper {
  position: relative;
}

.native-select {
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 2.5rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s;
}

.native-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.native-select:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.6;
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #3b82f6;
  pointer-events: none;
  font-size: 1.25rem;
}

.loading-text {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.95rem;
}

.embed-wrapper {
  margin-top: 2.5rem;
  animation: fadeInUp 0.6s ease-out;
}

.embed-loading-card {
  border-radius: 24px !important;
  background: linear-gradient(135deg, #f3e8ff 0%, #e0e7ff 100%);
  box-shadow: 0 20px 40px rgba(109, 40, 217, 0.15) !important;
}

.embed-report-card {
  border-radius: 24px !important;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(109, 40, 217, 0.1);
}

.embed-header {
  background: linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
}

.report-title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.report-subtitle {
  margin: 4px 0 0 0;
  font-size: 0.95rem;
  opacity: 0.95;
  font-weight: 500;
}

.powerbi-embed-container {
  width: 100vw;
  height: 100vh;
  min-height: 650px;
  background: #ffffff;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
}

.powerbi-embed-container:fullscreen {
  background-color: #ffffff;
  width: 100vw;
  height: 100vh;
}

.powerbi-embed-container:-webkit-full-screen {
  background-color: #ffffff;
}

.empty-state-card {
  margin-top: 3rem;
  border-radius: 24px;
  background: white;
  border: 3px dashed #cbd5e1;
  transition: all 0.3s ease;
}

.empty-state-card:hover {
  border-color: #6d28d9;
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(109, 40, 217, 0.12);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .powerbi-embed-container {
    height: 75vh;
    min-height: 550px;
  }

  .report-title {
    font-size: 1.4rem;
  }
}

@media (max-width: 600px) {
  .embed-header {
    padding: 1.2rem 1.5rem;
  }

  .powerbi-embed-container {
    height: 70vh;
  }
}
</style>